import React, { useState, useEffect } from 'react';
import { StoreSiteType } from '../../../types/site';
import { siteService } from '../../../services/siteService';
import { authService } from '../../../services/auth/authService';
import Modal from '../../shared/Modal';

const SiteTypesCard: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [siteTypes, setSiteTypes] = useState<StoreSiteType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<StoreSiteType | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', active: true });
  const [saving, setSaving] = useState(false);

  // Add modal state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', description: '', active: true });

  const storeId = authService.getStoreId();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const types = await siteService.getStoreSiteTypes();
      setSiteTypes(types);
      setError(null);
    } catch (err) {
      setError('Failed to load site types');
      console.error('Error loading site types:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (typeId: number, typeName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${typeName}"? This cannot be undone.`)) {
      return;
    }
    try {
      await siteService.deleteStoreSiteType(typeId);
      await loadData();
    } catch (err) {
      console.error('Error deleting site type:', err);
      alert('Failed to delete. The site type may be in use by existing sites.');
    }
  };

  const handleEdit = (siteType: StoreSiteType) => {
    setEditingType(siteType);
    setEditForm({
      name: siteType.name,
      description: siteType.description || '',
      active: siteType.active
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingType) return;

    try {
      setSaving(true);
      await siteService.updateStoreSiteType(editingType.id, {
        name: editForm.name.trim(),
        description: editForm.description.trim() || undefined,
        active: editForm.active
      });
      await loadData();
      setEditModalOpen(false);
      setEditingType(null);
    } catch (err) {
      console.error('Error updating site type:', err);
      alert('Failed to update site type. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingType(null);
  };

  const handleOpenAddModal = () => {
    setAddForm({
      name: '',
      description: '',
      active: true
    });
    setAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
  };

  const handleSaveAdd = async () => {
    if (!addForm.name.trim()) {
      alert('Please enter a site type name');
      return;
    }

    try {
      setSaving(true);
      await siteService.createStoreSiteType({
        store_id: storeId,
        name: addForm.name.trim(),
        description: addForm.description.trim() || undefined,
        active: addForm.active
      });
      await loadData();
      setAddModalOpen(false);
    } catch (err) {
      console.error('Error adding site type:', err);
      alert('Failed to add site type. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="config-card">
      <div
        className="config-card-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="config-card-title">
          <span className="config-card-icon">🏕️</span>
          <h2>Site Types</h2>
        </div>
        <div className="config-card-header-right">
          <span className="config-card-db-label">campground_db/store_site_types</span>
          <span className={`config-card-toggle ${isExpanded ? 'expanded' : ''}`}>
            ▼
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="config-card-content">
          {loading ? (
            <div className="config-loading">Loading site types...</div>
          ) : error ? (
            <div className="config-error">{error}</div>
          ) : siteTypes.length === 0 ? (
            <div className="config-empty">
              <p>No site types configured. Add site types to categorize your sites.</p>
              <button className="btn btn-primary btn-sm" onClick={handleOpenAddModal}>+ Add Site Type</button>
            </div>
          ) : (
            <>
              <table className="config-table">
                <thead>
                  <tr>
                    <th>Type Name</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {siteTypes.map(type => (
                    <tr key={type.id}>
                      <td>{type.name}</td>
                      <td>{type.description || '-'}</td>
                      <td className={type.active ? 'status-active' : 'status-inactive'}>
                        {type.active ? 'Active' : 'Inactive'}
                      </td>
                      <td>
                        <div className="config-actions">
                          <button
                            className="btn btn-text btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(type);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-text btn-sm btn-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(type.id, type.name);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="config-card-footer">
                <button className="btn btn-primary btn-sm" onClick={handleOpenAddModal}>+ Add Site Type</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={handleCloseEditModal}
        title={`Edit Site Type: ${editingType?.name || ''}`}
        size="small"
        footer={
          <>
            <button className="btn btn-secondary" onClick={handleCloseEditModal} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveEdit} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </>
        }
      >
        <div className="edit-form">
          <div className="form-group">
            <label htmlFor="site-type-name">Type Name</label>
            <input
              id="site-type-name"
              type="text"
              value={editForm.name}
              onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., RV Full Hookup, Tent Site, Cabin"
            />
          </div>
          <div className="form-group">
            <label htmlFor="site-type-description">Description</label>
            <textarea
              id="site-type-description"
              value={editForm.description}
              onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter a description for this site type (optional)"
              rows={3}
            />
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={editForm.active}
                onChange={(e) => setEditForm(prev => ({ ...prev, active: e.target.checked }))}
              />
              Active
            </label>
            <small className="form-help">Inactive site types will not be available for new sites.</small>
          </div>
        </div>
      </Modal>

      {/* Add Modal */}
      <Modal
        isOpen={addModalOpen}
        onClose={handleCloseAddModal}
        title="Add Site Type"
        size="small"
        footer={
          <>
            <button className="btn btn-secondary" onClick={handleCloseAddModal} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveAdd} disabled={saving}>
              {saving ? 'Adding...' : 'Add Site Type'}
            </button>
          </>
        }
      >
        <div className="edit-form">
          <div className="form-group">
            <label htmlFor="add-site-type-name">Type Name *</label>
            <input
              id="add-site-type-name"
              type="text"
              value={addForm.name}
              onChange={(e) => setAddForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., RV Full Hookup, Tent Site, Cabin"
            />
          </div>
          <div className="form-group">
            <label htmlFor="add-site-type-description">Description</label>
            <textarea
              id="add-site-type-description"
              value={addForm.description}
              onChange={(e) => setAddForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter a description for this site type (optional)"
              rows={3}
            />
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={addForm.active}
                onChange={(e) => setAddForm(prev => ({ ...prev, active: e.target.checked }))}
              />
              Active
            </label>
            <small className="form-help">Set as active to make this site type available for new sites.</small>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SiteTypesCard;
